import { css } from 'emotion';
import queryString from 'query-string';
import React, {
  MouseEvent,
  ReactElement,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Link } from 'react-router-dom';
import Field from 'redux-form/lib/Field';
import { OptimizelyContext } from '@optimizely/react-sdk';
import serverRenderingUtils from '../../../helpers/serverRenderingUtils';
import { useScript } from '../../hooks/useScript';
import { Button } from '../../shared/Button/NewButton';
import { AccessibleErrorContainer } from '../AccessibleErrorContainer';
import { AppleIcon } from '../AppleIcon';
import { FacebookIcon } from '../FacebookIcon';
import { FieldInputWithRedux } from '../FieldInput';
import { GoogleIcon } from '../GoogleIcon';
import OutboundLink from '../OutboundLink';
import { TwitterIcon } from '../TwitterIcon';
import events from './events';
import {
  ButtonWrapper,
  ErrorAlert,
  FieldWrapper,
  TextButton,
  LoginFormWrapper,
  SocialLoginContainer,
  SocialSignInBorderCSS,
} from './styles';

type Props = {
  clickRequestResetPassword: () => void;
  error: string;
  handleSubmit: () => void;
  submitting: boolean;
  showHelpLinks?: boolean;
  showSocialLogin?: boolean;
  feedUrl: string;
  signupUrl: string;
  showSignUp?: boolean;
};

// Global var for the Sign In With Apple API
declare let AppleID: any;

function getAppleAuthError() {
  if (serverRenderingUtils.windowUndefined()) return null;
  const { authappleerror } = queryString.parse(window.location.search);
  return authappleerror;
}

function buildQueryString(params: { [key: string]: string }) {
  const queryParams = Object.entries(params).filter(([key, value]) => !!value);
  const searchParamsString = new URLSearchParams(queryParams).toString();
  return searchParamsString ? `?${searchParamsString}` : '';
}

export function Login(props: Props) {
  const {
    error,
    handleSubmit,
    submitting,
    showHelpLinks = true,
    showSocialLogin = true,
    feedUrl: feedUrlString,
    signupUrl,
    showSignUp = true,
  } = props;
  const { optimizely } = useContext(OptimizelyContext);
  const isOptimizelyReady = optimizely?.isReady;
  const optimizelyUserId = optimizely?.user.id || '';
  const feedUrl = feedUrlString ? encodeURIComponent(feedUrlString) : '';
  const queryParams = {
    feedUrl,
    optimizelyUserId, // @TODO remove after age gating experiment?
  };
  const [urlQuery, setUrlQuery] = useState(() => buildQueryString(queryParams));
  const [appleIdHasLoaded, setAppleIdHasLoaded] = useState(false);

  useEffect(() => {
    // @TODO remove after age gating experiment?
    // Ensure optimizelyUserId is added to the urlQuery when available
    if (optimizely && isOptimizelyReady && !optimizelyUserId) {
      const removeUserListener = optimizely.onUserUpdate(() => {
        const userId = optimizely.user.id;
        if (userId) {
          setUrlQuery(
            buildQueryString({
              feedUrl,
              optimizelyUserId: userId,
            })
          );
        }
      });

      // Remove listener on cleanup
      return () => {
        removeUserListener();
      };
    }
  }, [feedUrl, isOptimizelyReady, optimizely, optimizelyUserId]);

  const authTwitterUrl = `/auth/twitter${urlQuery}`;
  const authFacebookUrl = `/login/facebook${urlQuery}`;
  const authGoogleUrl = `/login/google${urlQuery}`;
  const authAppleError = getAppleAuthError();

  useScript(
    'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js',
    () => {
      const redirectURI = new URL(
        '/auth/apple/callback',
        serverRenderingUtils.getBaseUrl()
      );
      AppleID.auth.init({
        clientId: 'com.anchorfminc.anchor.siwa',
        scope: 'name email',
        redirectURI: redirectURI.href,
        state: urlQuery ? JSON.stringify(queryParams) : '',
      });
      setAppleIdHasLoaded(true);
    }
  );

  const handleClickRequestResetPassword = (evt: MouseEvent) => {
    const { clickRequestResetPassword } = props;
    evt.preventDefault();
    clickRequestResetPassword();
  };

  // TODO: better handle errors
  let errorString: string | ReactElement = '';
  if (error) {
    try {
      const errorObj = JSON.parse(error);
      if (errorObj.status === 401) {
        errorString = 'Invalid username or password.';
      } else if (errorObj.status === 403) {
        errorString =
          "This account has been disabled. <a href='https://help.anchor.fm/hc/en-us'>Contact us</a> if you'd like to enable it.";
      } else if (errorObj.status === 429) {
        errorString = 'Please try again later.';
      } else {
        errorString =
          'There was a problem logging you in. Please try again from this device.';
      }
    } catch (err) {
      errorString = error;
    }
  } else if (authAppleError) {
    errorString =
      'There was a problem logging you in. Please try again from this device.';
  }

  return (
    <LoginFormWrapper>
      <form
        onSubmit={handleSubmit}
        id="LoginForm"
        method="POST"
        noValidate={true}
      >
        <FieldWrapper>
          <Field
            name="email"
            placeholder="Email"
            component={FieldInputWithRedux}
            type="email"
            aria-describedby="login-email-field"
          />
        </FieldWrapper>
        <FieldWrapper>
          <Field
            name="password"
            placeholder="Password"
            component={FieldInputWithRedux}
            type="password"
            aria-describedby="login-password-field"
          />
        </FieldWrapper>
        {errorString && (
          <AccessibleErrorContainer>
            <ErrorAlert>
              <div dangerouslySetInnerHTML={{ __html: errorString }} />
            </ErrorAlert>
          </AccessibleErrorContainer>
        )}
        <ButtonWrapper>
          <Button
            className={css`
              width: 100%;
            `}
            isDisabled={submitting}
            type="submit"
            color="purple"
          >
            Log in
          </Button>
        </ButtonWrapper>
      </form>
      {showHelpLinks && (
        <TextButton onClick={handleClickRequestResetPassword}>
          Forgot password?
        </TextButton>
      )}
      {showSocialLogin && (
        <div aria-label="socialLoginOptions">
          <p
            id="socialLoginOptions"
            className={css`
              margin-bottom: 12px;
            `}
          >
            Or log in with:
          </p>
          <SocialLoginContainer>
            <li>
              <OutboundLink
                onClick={events.trackSocialClick}
                to={authFacebookUrl}
                className={css`
                  ${SocialSignInBorderCSS}
                  color: #39579b;
                `}
                aria-label="Sign in with Facebook"
              >
                <FacebookIcon width={18} height={18} />
              </OutboundLink>
            </li>
            <li>
              <OutboundLink
                onClick={events.trackSocialClick}
                to={authGoogleUrl}
                className={css`
                  ${SocialSignInBorderCSS}
                  color: #8c8c8c;
                `}
                aria-label="Sign in with Google"
              >
                <GoogleIcon width={18} height={18} />
              </OutboundLink>
            </li>
            <li>
              <OutboundLink
                onClick={events.trackSocialClick}
                to={authTwitterUrl}
                className={css`
                  ${SocialSignInBorderCSS}
                  color: #50abf1;
                `}
                aria-label="Sign in with Twitter"
              >
                <TwitterIcon width={22} height={18} />
              </OutboundLink>
            </li>
            {appleIdHasLoaded && (
              <li>
                <button
                  onClick={() => {
                    events.trackSocialClick();
                    AppleID.auth.signIn();
                  }}
                  className={SocialSignInBorderCSS}
                  aria-label="Sign in with Apple"
                >
                  <AppleIcon
                    style={{ marginTop: '-4px' }}
                    width={24}
                    height={24}
                  />
                </button>
              </li>
            )}
          </SocialLoginContainer>
        </div>
      )}
      {showHelpLinks && (
        <div
          className={css`
            text-align: center;
          `}
        >
          {showSignUp && (
            <p>
              Don{`'`}t have an account?{' '}
              <Link to={signupUrl || `/signup${urlQuery}`}>Sign up</Link>{' '}
              instead.
            </p>
          )}
        </div>
      )}
    </LoginFormWrapper>
  );
}
